import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, Settings, Palette, Mail, Shield, Receipt, Bell, 
  Key, Flag, AlertTriangle, Save, TestTube, Building2, Check
} from 'lucide-react';
import { toast } from 'sonner';

const PlatformSettings = () => {
  const { fetchWithAuth, isSuperAdmin } = usePlatformAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState('branding');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/platform/settings');
      if (!response.ok) throw new Error('Failed to load settings');
      const data = await response.json();
      setSettings(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (section, data) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      const response = await fetchWithAuth(`/api/platform/settings/${section}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save settings');
      }

      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved`);
      loadSettings();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  const updateNestedSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </PlatformLayout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <PlatformLayout>
        <Alert className="bg-red-500/10 border-red-500/20">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-red-300">
            Only Super Admins can access platform settings.
          </AlertDescription>
        </Alert>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Platform Settings</h1>
          <p className="text-slate-400 mt-1">Configure your FinOrchest platform</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700 p-1 flex-wrap h-auto">
            <TabsTrigger value="branding" className="data-[state=active]:bg-blue-600">
              <Palette className="w-4 h-4 mr-2" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-blue-600">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-blue-600">
              <Receipt className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-blue-600">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-blue-600">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="api" className="data-[state=active]:bg-blue-600">
              <Key className="w-4 h-4 mr-2" />
              API Config
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-blue-600">
              <Flag className="w-4 h-4 mr-2" />
              Features
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="data-[state=active]:bg-blue-600">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Maintenance
            </TabsTrigger>
          </TabsList>

          {/* Branding */}
          <TabsContent value="branding">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Branding Settings
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Customize the look and feel of your platform
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Platform Name</Label>
                    <Input
                      value={settings?.branding?.platform_name || ''}
                      onChange={(e) => updateNestedSetting('branding', 'platform_name', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Tagline</Label>
                    <Input
                      value={settings?.branding?.tagline || ''}
                      onChange={(e) => updateNestedSetting('branding', 'tagline', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings?.branding?.primary_color || '#3B82F6'}
                        onChange={(e) => updateNestedSetting('branding', 'primary_color', e.target.value)}
                        className="w-14 h-10 p-1 bg-slate-700/50 border-slate-600"
                      />
                      <Input
                        value={settings?.branding?.primary_color || '#3B82F6'}
                        onChange={(e) => updateNestedSetting('branding', 'primary_color', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings?.branding?.secondary_color || '#8B5CF6'}
                        onChange={(e) => updateNestedSetting('branding', 'secondary_color', e.target.value)}
                        className="w-14 h-10 p-1 bg-slate-700/50 border-slate-600"
                      />
                      <Input
                        value={settings?.branding?.secondary_color || '#8B5CF6'}
                        onChange={(e) => updateNestedSetting('branding', 'secondary_color', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Support Email</Label>
                    <Input
                      type="email"
                      value={settings?.branding?.support_email || ''}
                      onChange={(e) => updateNestedSetting('branding', 'support_email', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Support Phone</Label>
                    <Input
                      value={settings?.branding?.support_phone || ''}
                      onChange={(e) => updateNestedSetting('branding', 'support_phone', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="+91 99999 99999"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => saveSection('branding', settings.branding)}
                  disabled={saving.branding}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.branding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Branding
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Security Policies
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure authentication and security requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-white">Two-Factor Authentication</h4>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="text-white">Enforce 2FA for All Users</p>
                      <p className="text-sm text-slate-400">Require all users to enable 2FA</p>
                    </div>
                    <Switch
                      checked={settings?.security?.enforce_2fa_all_users || false}
                      onCheckedChange={(checked) => updateNestedSetting('security', 'enforce_2fa_all_users', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="text-white">Enforce 2FA for Admins Only</p>
                      <p className="text-sm text-slate-400">Only require 2FA for admin users</p>
                    </div>
                    <Switch
                      checked={settings?.security?.enforce_2fa_admins_only || false}
                      onCheckedChange={(checked) => updateNestedSetting('security', 'enforce_2fa_admins_only', checked)}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-white">Password Policy</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Minimum Password Length</Label>
                      <Input
                        type="number"
                        value={settings?.security?.min_password_length || 8}
                        onChange={(e) => updateNestedSetting('security', 'min_password_length', parseInt(e.target.value))}
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Session Timeout (minutes)</Label>
                      <Input
                        type="number"
                        value={settings?.security?.session_timeout_minutes || 480}
                        onChange={(e) => updateNestedSetting('security', 'session_timeout_minutes', parseInt(e.target.value))}
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Max Login Attempts</Label>
                      <Input
                        type="number"
                        value={settings?.security?.max_login_attempts || 5}
                        onChange={(e) => updateNestedSetting('security', 'max_login_attempts', parseInt(e.target.value))}
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Lockout Duration (minutes)</Label>
                      <Input
                        type="number"
                        value={settings?.security?.lockout_duration_minutes || 30}
                        onChange={(e) => updateNestedSetting('security', 'lockout_duration_minutes', parseInt(e.target.value))}
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => saveSection('security', settings.security)}
                  disabled={saving.security}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.security ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Security Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing */}
          <TabsContent value="billing">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Billing Configuration
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure invoice and payment settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Default GST Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings?.billing?.default_gst_rate || 18}
                      onChange={(e) => updateNestedSetting('billing', 'default_gst_rate', parseFloat(e.target.value))}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Invoice Prefix</Label>
                    <Input
                      value={settings?.billing?.invoice_prefix || 'INV'}
                      onChange={(e) => updateNestedSetting('billing', 'invoice_prefix', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Payment Terms (days)</Label>
                    <Input
                      type="number"
                      value={settings?.billing?.payment_terms_days || 7}
                      onChange={(e) => updateNestedSetting('billing', 'payment_terms_days', parseInt(e.target.value))}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Grace Period (days)</Label>
                    <Input
                      type="number"
                      value={settings?.billing?.grace_period_days || 3}
                      onChange={(e) => updateNestedSetting('billing', 'grace_period_days', parseInt(e.target.value))}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Invoice Footer Text</Label>
                  <Textarea
                    value={settings?.billing?.invoice_footer_text || ''}
                    onChange={(e) => updateNestedSetting('billing', 'invoice_footer_text', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    rows={2}
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                  <div>
                    <p className="text-white">Auto-suspend on Overdue</p>
                    <p className="text-sm text-slate-400">Automatically suspend accounts with overdue payments</p>
                  </div>
                  <Switch
                    checked={settings?.billing?.auto_suspend_on_overdue || false}
                    onCheckedChange={(checked) => updateNestedSetting('billing', 'auto_suspend_on_overdue', checked)}
                  />
                </div>
                <Button 
                  onClick={() => saveSection('billing', settings.billing)}
                  disabled={saving.billing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.billing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Billing Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email */}
          <TabsContent value="email">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Configuration
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure email service and templates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Provider</Label>
                  <Select
                    value={settings?.email?.provider || 'NONE'}
                    onValueChange={(value) => updateNestedSetting('email', 'provider', value)}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="NONE" className="text-white">None (Disabled)</SelectItem>
                      <SelectItem value="SENDGRID" className="text-white">SendGrid</SelectItem>
                      <SelectItem value="RESEND" className="text-white">Resend</SelectItem>
                      <SelectItem value="SMTP" className="text-white">Custom SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings?.email?.provider === 'SENDGRID' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">SendGrid API Key</Label>
                    <Input
                      type="password"
                      value={settings?.email?.sendgrid_api_key || ''}
                      onChange={(e) => updateNestedSetting('email', 'sendgrid_api_key', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="SG.xxxxx"
                    />
                  </div>
                )}

                {settings?.email?.provider === 'RESEND' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Resend API Key</Label>
                    <Input
                      type="password"
                      value={settings?.email?.resend_api_key || ''}
                      onChange={(e) => updateNestedSetting('email', 'resend_api_key', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      placeholder="re_xxxxx"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Sender Name</Label>
                    <Input
                      value={settings?.email?.sender_name || ''}
                      onChange={(e) => updateNestedSetting('email', 'sender_name', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Sender Email</Label>
                    <Input
                      type="email"
                      value={settings?.email?.sender_email || ''}
                      onChange={(e) => updateNestedSetting('email', 'sender_email', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-white">Email Templates</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'welcome_email_enabled', label: 'Welcome Email' },
                      { key: 'invoice_email_enabled', label: 'Invoice Email' },
                      { key: 'payment_reminder_enabled', label: 'Payment Reminders' },
                      { key: 'payout_notification_enabled', label: 'Payout Notifications' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                        <span className="text-white">{label}</span>
                        <Switch
                          checked={settings?.email?.[key] || false}
                          onCheckedChange={(checked) => updateNestedSetting('email', key, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => saveSection('email', settings.email)}
                  disabled={saving.email}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.email ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Email Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure webhooks and alert triggers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                    <div>
                      <p className="text-white">Slack Notifications</p>
                      <p className="text-sm text-slate-400">Send alerts to Slack channel</p>
                    </div>
                    <Switch
                      checked={settings?.notifications?.slack_enabled || false}
                      onCheckedChange={(checked) => updateNestedSetting('notifications', 'slack_enabled', checked)}
                    />
                  </div>
                  {settings?.notifications?.slack_enabled && (
                    <div className="space-y-2">
                      <Label className="text-slate-300">Slack Webhook URL</Label>
                      <Input
                        value={settings?.notifications?.slack_webhook_url || ''}
                        onChange={(e) => updateNestedSetting('notifications', 'slack_webhook_url', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white"
                        placeholder="https://hooks.slack.com/services/..."
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-white">Alert Triggers</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'alert_on_new_signup', label: 'New Customer Signup' },
                      { key: 'alert_on_churn', label: 'Customer Churn' },
                      { key: 'alert_on_failed_payment', label: 'Failed Payment' },
                      { key: 'alert_on_high_value_payout', label: 'High Value Payout' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                        <span className="text-white">{label}</span>
                        <Switch
                          checked={settings?.notifications?.[key] || false}
                          onCheckedChange={(checked) => updateNestedSetting('notifications', key, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => saveSection('notifications', settings.notifications)}
                  disabled={saving.notifications}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.notifications ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Notification Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Configuration */}
          <TabsContent value="api">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  API Configuration
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure payment gateway and banking API credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Razorpay */}
                <div className="space-y-4">
                  <h4 className="font-medium text-white">Razorpay (Subscriptions)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Key ID</Label>
                      <Input
                        value={settings?.api?.razorpay_key_id || ''}
                        onChange={(e) => updateNestedSetting('api', 'razorpay_key_id', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        placeholder="rzp_live_xxxxx"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Key Secret</Label>
                      <Input
                        type="password"
                        value={settings?.api?.razorpay_key_secret || ''}
                        onChange={(e) => updateNestedSetting('api', 'razorpay_key_secret', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Cashfree */}
                <div className="space-y-4">
                  <h4 className="font-medium text-white">Cashfree (Payouts)</h4>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Banking Provider</Label>
                    <Select
                      value={settings?.api?.banking_provider || 'MOCK'}
                      onValueChange={(value) => updateNestedSetting('api', 'banking_provider', value)}
                    >
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="MOCK" className="text-white">Mock (Testing)</SelectItem>
                        <SelectItem value="CASHFREE" className="text-white">Cashfree</SelectItem>
                        <SelectItem value="RAZORPAYX" className="text-white">RazorpayX</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {settings?.api?.banking_provider === 'CASHFREE' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-slate-300">Client ID</Label>
                          <Input
                            value={settings?.api?.cashfree_client_id || ''}
                            onChange={(e) => updateNestedSetting('api', 'cashfree_client_id', e.target.value)}
                            className="bg-slate-700/50 border-slate-600 text-white font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">Client Secret</Label>
                          <Input
                            type="password"
                            value={settings?.api?.cashfree_client_secret || ''}
                            onChange={(e) => updateNestedSetting('api', 'cashfree_client_secret', e.target.value)}
                            className="bg-slate-700/50 border-slate-600 text-white"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Environment</Label>
                        <Select
                          value={settings?.api?.cashfree_environment || 'SANDBOX'}
                          onValueChange={(value) => updateNestedSetting('api', 'cashfree_environment', value)}
                        >
                          <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="SANDBOX" className="text-white">Sandbox (Testing)</SelectItem>
                            <SelectItem value="PRODUCTION" className="text-white">Production</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {settings?.api?.banking_provider === 'MOCK' && (
                    <Alert className="bg-yellow-500/10 border-yellow-500/20">
                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                      <AlertDescription className="text-yellow-300">
                        Banking is in mock mode. Payouts will be simulated, not real.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                <Button 
                  onClick={() => saveSection('api', settings.api)}
                  disabled={saving.api}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.api ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save API Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Flags */}
          <TabsContent value="features">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Flag className="w-5 h-5" />
                  Feature Flags
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Enable or disable platform features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'demo_accounts_enabled', label: 'Demo Accounts', desc: 'Allow users to try with demo accounts' },
                    { key: 'referral_program_enabled', label: 'Referral Program', desc: 'Enable referral and coupon system' },
                    { key: 'virtual_accounts_enabled', label: 'Virtual Accounts', desc: 'Allow creating virtual bank accounts' },
                    { key: 'api_access_enabled', label: 'API Access', desc: 'Allow customers to use API (Coming Soon)', badge: 'Soon' },
                    { key: 'audit_logs_ui_enabled', label: 'Audit Logs UI', desc: 'Show audit logs to customers (Coming Soon)', badge: 'Soon' },
                    { key: 'bulk_payouts_enabled', label: 'Bulk Payouts', desc: 'Allow bulk payment uploads (Coming Soon)', badge: 'Soon' },
                  ].map(({ key, label, desc, badge }) => (
                    <div key={key} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white">{label}</p>
                          {badge && <Badge className="bg-amber-500/10 text-amber-400 text-xs">{badge}</Badge>}
                        </div>
                        <p className="text-sm text-slate-400">{desc}</p>
                      </div>
                      <Switch
                        checked={settings?.features?.[key] || false}
                        onCheckedChange={(checked) => updateNestedSetting('features', key, checked)}
                        disabled={badge === 'Soon'}
                      />
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Demo Duration (days)</Label>
                  <Input
                    type="number"
                    value={settings?.features?.demo_duration_days || 2}
                    onChange={(e) => updateNestedSetting('features', 'demo_duration_days', parseInt(e.target.value))}
                    className="bg-slate-700/50 border-slate-600 text-white w-32"
                  />
                </div>

                <Button 
                  onClick={() => saveSection('features', settings.features)}
                  disabled={saving.features}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.features ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Feature Flags
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance */}
          <TabsContent value="maintenance">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Maintenance Mode
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure scheduled downtime and maintenance notices
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div>
                    <p className="text-white font-medium">Maintenance Mode</p>
                    <p className="text-sm text-red-300">Enabling this will show a maintenance page to all users</p>
                  </div>
                  <Switch
                    checked={settings?.maintenance?.maintenance_mode || false}
                    onCheckedChange={(checked) => updateNestedSetting('maintenance', 'maintenance_mode', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Maintenance Message</Label>
                  <Textarea
                    value={settings?.maintenance?.maintenance_message || ''}
                    onChange={(e) => updateNestedSetting('maintenance', 'maintenance_message', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Scheduled Downtime Notice</Label>
                  <Textarea
                    value={settings?.maintenance?.scheduled_downtime_notice || ''}
                    onChange={(e) => updateNestedSetting('maintenance', 'scheduled_downtime_notice', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    rows={2}
                    placeholder="Optional notice shown to users about upcoming maintenance"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                  <div>
                    <p className="text-white">Allow Admin Access During Maintenance</p>
                    <p className="text-sm text-slate-400">Platform admins can still access during maintenance</p>
                  </div>
                  <Switch
                    checked={settings?.maintenance?.allow_admin_access || true}
                    onCheckedChange={(checked) => updateNestedSetting('maintenance', 'allow_admin_access', checked)}
                  />
                </div>

                <Button 
                  onClick={() => saveSection('maintenance', settings.maintenance)}
                  disabled={saving.maintenance}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving.maintenance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Maintenance Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PlatformLayout>
  );
};

export default PlatformSettings;
